import { api } from './client';

export type ParameterSetStatus = 'Draft' | 'Active' | 'Superseded';

export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  language: string;
  emailConfirmed: boolean;
  isDisabled: boolean;
  isAdmin: boolean;
  tier: string;
  effectiveTier: string;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  /** Only the last four digits ever leave the server. */
  egnLast4: string | null;
  bulstat: string | null;
  onboardingCompleted: boolean;
  incomeEntryCount: number;
  obligationCount: number;
  declarationCount: number;
  /** "google / cpc · campaign", a referring host, or null when the visitor did not agree to analytics. */
  cameFrom: string | null;
}

export interface Paged<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface AdminMetrics {
  totalUsers: number;
  confirmedUsers: number;
  disabledUsers: number;
  onboardedUsers: number;
  activeTrials: number;
  payingUsers: number;
  dailyActive: number;
  weeklyActive: number;
  signupsByDay: { date: string; count: number }[];
}

export interface ParameterSet {
  id: string;
  year: number;
  version: number;
  status: ParameterSetStatus;
  activatedAt: string | null;
  notes: string | null;
  dataJson: string;
}

export interface ParameterDiff {
  year: number;
  comparedToVersion: number | null;
  changes: { path: string; before: string | null; after: string | null }[];
}

export const adminApi = {
  users: (query: string, page = 1) =>
    api<Paged<AdminUser>>(
      `/api/admin/users?page=${page}${query ? `&query=${encodeURIComponent(query)}` : ''}`,
    ),

  user: (id: string) => api<AdminUser>(`/api/admin/users/${id}`),

  setDisabled: (id: string, disabled: boolean) =>
    api<void>(`/api/admin/users/${id}/disabled`, {
      method: 'POST',
      body: JSON.stringify({ disabled }),
    }),

  metrics: () => api<AdminMetrics>('/api/admin/metrics'),

  parameterSets: () => api<ParameterSet[]>('/api/admin/parameter-sets'),

  createDraft: (year: number) =>
    api<ParameterSet>(`/api/admin/parameter-sets/${year}/draft`, { method: 'POST' }),

  saveDraft: (id: string, dataJson: string, notes: string | null) =>
    api<ParameterSet>(`/api/admin/parameter-sets/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ dataJson, notes }),
    }),

  diff: (id: string) => api<ParameterDiff>(`/api/admin/parameter-sets/${id}/diff`),

  activate: (id: string) =>
    api<ParameterSet>(`/api/admin/parameter-sets/${id}/activate`, { method: 'POST' }),
};

export type SystemAlertKind = 'LegalSourceChanged' | 'LegalSourceUnreachable' | 'ParameterSetMissing';
export type SystemAlertSeverity = 'Info' | 'Warning' | 'Critical';

export interface SystemAlert {
  id: string;
  kind: SystemAlertKind;
  severity: SystemAlertSeverity;
  title: string;
  detail: string;
  url: string | null;
  /** How many checks have seen the condition since it was last acknowledged. */
  occurrences: number;
  raisedAt: string;
  lastSeenAt: string;
  acknowledgedAt: string | null;
}

export interface LegalSourceWatch {
  id: string;
  key: string;
  url: string;
  description: string;
  isEnabled: boolean;
  lastCheckedAt: string | null;
  lastChangedAt: string | null;
  consecutiveFailures: number;
  lastError: string | null;
}

export const monitoringApi = {
  alerts: (includeAcknowledged: boolean) =>
    api<SystemAlert[]>(`/api/admin/alerts?includeAcknowledged=${includeAcknowledged}`),

  acknowledge: (id: string) =>
    api<void>(`/api/admin/alerts/${id}/acknowledge`, { method: 'POST' }),

  sources: () => api<LegalSourceWatch[]>('/api/admin/legal-sources'),

  checkNow: () => api<{ opened: number }>('/api/admin/legal-sources/check', { method: 'POST' }),
};

export const authApi = {
  login: (email: string, password: string) =>
    api<void>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, rememberMe: true }),
    }),

  logout: () => api<void>('/api/auth/logout', { method: 'POST' }),

  me: () => api<{ id: string; email: string; isAdmin: boolean }>('/api/auth/me'),
};
